import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  mutationEditorAggregatedAnswers,
  mutationEditorClear,
  mutationEditorQuestionUpdate,
  mutationEditorQuestionSelect,
  mutationEditorQuestionSelectMultiple,
  mutationEditorQuestionMove,
  mutationEditorQuestionPreview,
  mutationEditorQuestionComments,
  mutationEditorStudentErrors,
} from 'gql/mutations/editor';
import {
  useMutationQuestionDelete,
  useMutationQuestionDuplicate,
} from 'gql/mutations/question';
import { trackEvent, reportError } from 'lib/tracking';
import { getQuestionInfo } from 'questions';
import { notify } from 'uikit/Notifications';
import { useQueryMe } from 'gql/queries/me';

import QuestionCard from './QuestionCard';

const QuestionCardContainer = ({
  course,
  question,
  questions,
  questionsSelected,
  ...props
}) => {
  const { t } = useTranslation();
  const { data } = useQueryMe();
  const handleQuestionSelect = useCallback(
    (evt) => {
      evt.preventDefault();
      document.getSelection().removeAllRanges();
      const lastSelectedQuestionFromSameChapter = questionsSelected
        .filter(
          (questionSelected) =>
            questionSelected.chapterId === question.chapterId,
        )
        .pop();
      if (evt.shiftKey && lastSelectedQuestionFromSameChapter) {
        mutationEditorQuestionSelectMultiple({
          lastQuestion: lastSelectedQuestionFromSameChapter,
          question,
          questions,
        });
      } else {
        mutationEditorQuestionSelect(question);
      }
    },
    [question, questions, questionsSelected],
  );
  const handleQuestionClick = useCallback(() => {
    mutationEditorQuestionUpdate(question);
  }, [question]);
  const handleQuestionUpdate = useCallback(() => {
    mutationEditorQuestionUpdate(question);
  }, [question]);
  const mutationQuestionDelete = useMutationQuestionDelete();
  const handleQuestionDelete = useCallback(async () => {
    try {
      mutationEditorClear();
      await mutationQuestionDelete(question);
      trackEvent('question', 'delete');
    } catch (err) {
      notify(
        'error',
        t(
          'Course/Teacher/Editor/Finder/QuestionCard/QuestionDelete/question-delete-error',
        ),
      );
      reportError('question', 'delete', err);
    }
  }, [mutationQuestionDelete, question, t]);
  const mutationQuestionDuplicate = useMutationQuestionDuplicate();
  const handleQuestionDuplicate = useCallback(async () => {
    try {
      await mutationQuestionDuplicate(question);
      trackEvent('question', 'duplicate');
    } catch (err) {
      notify(
        'error',
        t(
          'Course/Teacher/Editor/Finder/QuestionCard/QuestionDuplicate/question-duplicate-error',
        ),
      );
      reportError('question', 'duplicate', err);
    }
  }, [mutationQuestionDuplicate, question, t]);
  const handleQuestionMove = useCallback(() => {
    mutationEditorQuestionMove(question);
  }, [question]);
  const handleQuestionPreview = useCallback(() => {
    mutationEditorQuestionPreview(question);
  }, [question]);
  const handleQuestionComments = useCallback(() => {
    mutationEditorQuestionComments(question);
  }, [question]);
  const questionInfo = getQuestionInfo(question.type);
  const handleStudentErrors = useCallback(() => {
    mutationEditorStudentErrors(question);
  }, [question]);
  const handleAnswersAggregate = useCallback(() => {
    mutationEditorAggregatedAnswers(question);
  }, [question]);
  const handleExpertLinkCopy = useCallback(() => {
    navigator.clipboard.writeText(
      `${process.env.REACT_APP_WEB_URL}/expert/${course.id}/${question.id}`,
    );
    notify(
      'success',
      t('Course/Teacher/Editor/Finder/QuestionCard/expert-link-duplicated'),
    );
  }, [course, question, t]);
  return (
    <QuestionCard
      handleAnswersAggregate={handleAnswersAggregate}
      handleExpertLinkCopy={handleExpertLinkCopy}
      handleQuestionClick={handleQuestionClick}
      handleQuestionComments={handleQuestionComments}
      handleQuestionDelete={handleQuestionDelete}
      handleQuestionDuplicate={handleQuestionDuplicate}
      handleQuestionMove={handleQuestionMove}
      handleQuestionPreview={handleQuestionPreview}
      handleQuestionSelect={handleQuestionSelect}
      handleQuestionUpdate={handleQuestionUpdate}
      handleStudentErrors={handleStudentErrors}
      me={data?.me}
      question={question}
      questionInfo={questionInfo}
      t={t}
      {...props}
    />
  );
};

QuestionCardContainer.propTypes = {
  className: PropTypes.string,
  course: PropTypes.object.isRequired,
  question: PropTypes.object.isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  questionsSelected: PropTypes.arrayOf(PropTypes.object).isRequired,
};

QuestionCardContainer.defaultProps = {
  className: undefined,
};

export default React.memo(QuestionCardContainer);
